import { ParametersDefaults, PriorityChannel } from "./type.mjs";
import { PriceType, cloneParameters, defaults, floorWith, getPriceMeter, isDITModelType, isDistillModelType, isSDOrSDXLModelStrict } from "./util.mjs";
import { GenerationModelTypeSubSet } from "./base.mjs";
import { PricingAdapter } from "./adapter.mjs";
//#region src/index.ts
var Pricing = class {
	parameters;
	config;
	adapter;
	constructor(parameters, config) {
		this.parameters = defaults(ParametersDefaults, parameters);
		this.config = defaults({
			baseStep: 20,
			baseWidth: 512,
			baseHeight: 512,
			basePrice: 400,
			maxDiscount: 800,
			samplerMultiple: {},
			sdxlMultiple: 1.25,
			sd3MediumMultiple: 1.625,
			ditMinPrice: 1e3,
			minPrice: 200,
			minPricePerSDImage: 150,
			minPricePerDitImage: 200,
			minPricePerImageForUnknownModel: 300,
			highPriority: PriorityChannel.HighPriority,
			xHighPriority: PriorityChannel.XHighPriority,
			shouldDiscount: false,
			isSDXL: false,
			isSD3Medium: false,
			modelType: ""
		}, config);
		this.adapter = new PricingAdapter(this.parameters, this.config);
	}
	_getBasePrice(config) {
		const { basePrice, modelType } = {
			...this.config,
			...config
		};
		let actualBasePrice = basePrice;
		if (isDITModelType(modelType)) if (!!this.parameters.mediaId) actualBasePrice = basePrice * 2;
		else {
			const { width, height, samplingSteps } = this.parameters;
			actualBasePrice = basePrice * ([
				[768, 1280],
				[1280, 768],
				[1024, 1024]
			].some(([w, h]) => w === width && h === height) && samplingSteps >= 20 && samplingSteps <= 25 ? 1.5 : 1.6);
		}
		return actualBasePrice;
	}
	_getMeterWithBasePrice(config) {
		const actualBasePrice = this._getBasePrice(config);
		const meter = getPriceMeter();
		meter.addItem(() => actualBasePrice, {
			key: "generate-image",
			name: "generate image"
		});
		return meter;
	}
	_animateDiffPrice(meter, parameters, config) {
		const { width, height, animateDiff } = parameters;
		const { baseWidth, baseHeight } = {
			...this.config,
			...config
		};
		const ratio = Math.min(1, 768 / Math.max(height, width));
		const sizePower = width * height * ratio * ratio / baseWidth / baseHeight;
		if (sizePower !== 1) meter.addItem((i) => i * sizePower, {
			key: "size",
			name: "size"
		});
		if (animateDiff.v2) meter.addItem((i) => i * 16, {
			key: "animate-v2",
			name: "animate-v2"
		});
		else {
			const animatePower = animateDiff.smooth ? 25 : 10;
			meter.addItem((i) => i * animatePower, {
				key: "animate",
				name: "animate"
			});
			const animateLongPower = animateDiff.long ? 3 : 1;
			if (animateLongPower !== 1) meter.addItem((i) => i * animateLongPower, {
				key: "animate-long",
				name: "long animation"
			});
		}
		return meter;
	}
	_diffusePrice(meter, parameters, config) {
		let { width, height, samplingSteps, upscaleDenoisingSteps = samplingSteps, samplingMethod, mediaId, strength, upscale: _upscale, batchSize, enableTile, controlNets } = parameters;
		let upscale = _upscale;
		if (!Number.isFinite(upscale) || upscale < 1) upscale = 1;
		const { baseStep, baseWidth, baseHeight, samplerMultiple, floorBeforeTimesBatchSize, sdxlMultiple, sd3MediumMultiple, isSDXL, isSD3Medium, modelType, pricingConfig } = {
			...this.config,
			...config
		};
		if (isDistillModelType(pricingConfig?.variant, parameters.modelId)) {
			samplingSteps = samplingSteps + 5;
			upscaleDenoisingSteps = upscaleDenoisingSteps + 5;
		}
		const sizePower = enableTile ? 1 : width * height * upscale * upscale / baseWidth / baseHeight;
		let equivalentStep = samplingSteps;
		if (!!mediaId) equivalentStep = 1 + strength * samplingSteps;
		else if (upscale > 1) equivalentStep = upscaleDenoisingSteps;
		else equivalentStep = samplingSteps;
		const stepPower = equivalentStep / baseStep;
		const samplerPower = samplerMultiple[samplingMethod] ?? 1;
		if (isDITModelType(modelType)) {
			const ditMultiple = sdxlMultiple;
			meter.addItem((i) => i * ditMultiple, {
				key: "dit",
				name: "dit"
			});
		} else if (isSDXL || modelType === GenerationModelTypeSubSet.SdxlModel) meter.addItem((i) => i * sdxlMultiple, {
			key: "sdxl",
			name: "sdxl"
		});
		else if (isSD3Medium || modelType === GenerationModelTypeSubSet.Sd3MediumModel) meter.addItem((i) => i * sd3MediumMultiple, {
			key: "sd3m",
			name: "sd3m"
		});
		if (sizePower !== 1) meter.addItem((i) => i * sizePower, {
			key: "size",
			name: "size"
		});
		if (stepPower !== 1) meter.addItem((i) => i * stepPower, {
			key: "sampling-steps",
			name: "sampling steps"
		});
		if (samplerPower !== 1) meter.addItem((i) => i * samplerPower, {
			key: "sampling-method",
			name: "sampling method"
		});
		if (Array.isArray(controlNets) && controlNets.length) meter.addItem((i) => i * 1.4 ** (controlNets.length - 1), {
			key: "control-nets",
			name: "control nets"
		});
		if (floorBeforeTimesBatchSize) meter.addItem((i) => floorWith(i, 100), {
			key: "floor",
			name: "floor",
			hidden: true
		});
		if (batchSize !== 1) meter.addItem((i) => i * batchSize, {
			key: "batch-size",
			name: "batch size"
		});
		return meter;
	}
	_priorityChannelPrice(meter, config) {
		const { priority } = this.parameters;
		const { highPriority, xHighPriority } = {
			...this.config,
			...config
		};
		if (priority >= highPriority || priority >= xHighPriority) {
			let priorityFee = highPriority;
			if (priority > highPriority) priorityFee = xHighPriority;
			if (priority > xHighPriority) priorityFee = priority;
			meter.addItem((i) => i + priorityFee, {
				key: "task-priority",
				name: "priority channel"
			});
		}
	}
	isAnimateDiff(parameters) {
		return parameters.animateDiff != null && parameters.animateDiff.enabled;
	}
	_pricingBaseParameters(meter, parameters, config) {
		const { height, width, enlarge, upscale, enableTile, batchSize, enableADetailer, modelId: modelVersionId, ipAdapter } = parameters;
		const { baseHeight, baseWidth, maxDiscount, shouldDiscount, modelVersionDiscount, minPrice, ditMinPrice, minPricePerSDImage, minPricePerDitImage, minPricePerImageForUnknownModel, modelType } = {
			...this.config,
			...config
		};
		const actualBasePrice = this._getBasePrice(config);
		if (this.isAnimateDiff(parameters)) {
			this._animateDiffPrice(meter, parameters, config);
			const discount = .5;
			meter.addItem((i) => i * discount, {
				key: "animate-discount",
				name: "animate discount"
			});
		} else if (enlarge > 1) {} else if (enableTile && upscale > 1) {
			const tileMeter = this._getMeterWithBasePrice();
			const tileTimes = Math.ceil(height * width * upscale * upscale / baseHeight / baseWidth);
			this._diffusePrice(tileMeter, parameters, config);
			const tilePrice = tileMeter.toPriceInfo();
			meter.addItem((i) => i + tilePrice.actualPrice * tileTimes, {
				key: "tile",
				name: "tile",
				unitPrice: tilePrice.actualPrice,
				items: tilePrice.items
			});
		} else if (upscale > 1) {
			this._diffusePrice(meter, {
				...parameters,
				upscale: 1
			}, config);
			const { modelType } = {
				...this.config,
				...config
			};
			let hiresMeter;
			if (isDITModelType(modelType)) hiresMeter = this._getMeterWithBasePrice({
				...config,
				modelType: GenerationModelTypeSubSet.SdxlModel
			});
			else hiresMeter = this._getMeterWithBasePrice(config);
			this._diffusePrice(hiresMeter, parameters, config);
			const hiresPrice = hiresMeter.toPriceInfo();
			const discount = .5;
			meter.addItem((i) => i + hiresPrice.actualPrice, {
				key: "upscale",
				name: "upscale",
				items: hiresPrice.items
			});
			meter.addItem((i) => i - hiresPrice.actualPrice * discount, {
				key: "upscale-discount",
				name: "upscale discount"
			});
		} else this._diffusePrice(meter, parameters, {
			...config,
			floorBeforeTimesBatchSize: true,
			baseStep: 28
		});
		if (enableADetailer) {
			const adMeter = this._getMeterWithBasePrice();
			this._diffusePrice(adMeter, {
				...parameters,
				height: 512,
				width: 512,
				upscale: 1,
				enlarge: 1,
				strength: .8,
				mediaId: "1",
				controlNets: []
			}, config);
			const adPrice = adMeter.toPriceInfo();
			meter.addItem((i) => i + adPrice.actualPrice, {
				key: "adetailer",
				name: "adetailer",
				unitPrice: adPrice.actualPrice
			});
		}
		meter.addItem((i) => floorWith(i - actualBasePrice * batchSize, 100), {
			key: "minus-base-price",
			name: "minus base price",
			hidden: true
		});
		if (batchSize > 1 && meter.curPrice > 0 && shouldDiscount) meter.addItem((i) => i * .5, {
			key: "email-verification-discount",
			name: "email verification discount"
		});
		if (ipAdapter && ipAdapter.enabled) meter.addItem((i) => i + 500, {
			key: "wdtagger",
			name: "wdtagger",
			unitPrice: 500
		});
		if (modelVersionId && modelVersionDiscount && modelVersionDiscount[modelVersionId]) {
			const discountPercent = modelVersionDiscount[modelVersionId] / 100;
			meter.addItem((i) => i * discountPercent, {
				key: "model-discount",
				name: "model discount"
			});
		}
		if (meter.curPrice < -maxDiscount) meter.addItem(() => -maxDiscount, {
			key: "max-discount",
			name: "max discount"
		});
		if (minPrice > 0 && meter.curPrice < minPrice) meter.addItem(() => minPrice, {
			key: "min-price",
			name: "min price"
		});
		if (batchSize <= 4) {
			if (isDITModelType(modelType) && meter.curPrice < ditMinPrice) meter.addItem(() => ditMinPrice, {
				key: "dit-min-price",
				name: "dit min price"
			});
		} else if (isSDOrSDXLModelStrict(modelType) && meter.curPrice < batchSize * minPricePerSDImage) {
			if (meter.curPrice < batchSize * minPricePerSDImage) meter.addItem(() => batchSize * minPricePerSDImage, {
				key: "sd-min-price-large-batch",
				name: "sd min price large batch"
			});
		} else if (isDITModelType(modelType) && meter.curPrice < batchSize * minPricePerDitImage) meter.addItem(() => batchSize * minPricePerDitImage, {
			key: "dit-min-price-large-batch",
			name: "dit min price large batch"
		});
		else if (meter.curPrice < batchSize * minPricePerImageForUnknownModel) meter.addItem(() => batchSize * minPricePerImageForUnknownModel, {
			key: "unknown-model-min-price-large-batch",
			name: "unknown model min price large batch"
		});
		this._priorityChannelPrice(meter, config);
		return meter;
	}
	_pricingPrepaidWorkflow(meter, workflowName, config) {
		const basePrice = this._getBasePrice(config);
		const { workflowPrepaidPrice } = {
			...this.config,
			...config
		};
		const price = workflowPrepaidPrice[workflowName];
		meter.addItem((i) => i - basePrice, {
			key: "minus-base-price",
			name: "minus base price"
		});
		meter.addItem((i) => i + price, {
			key: "workflow-prepaid-fixed",
			name: "workflow prepaid fixed"
		});
		return meter;
	}
	_pricingT2i2v(meter, parameters) {
		const { t2i2v, ...rest } = parameters;
		if (this._isChat(parameters)) {
			this._pricingChat(meter, parameters);
			const actualBasePrice = this._getBasePrice();
			meter.addItem((i) => i + actualBasePrice, {
				key: "t2i2v-chat-base-adjustment",
				name: "t2i2v chat base adjustment",
				hidden: true
			});
		}
		if ((parameters.upscale ?? 1) > 1) meter.addItem((i) => i + 6e3, {
			key: "t2i2v-upscale",
			name: "t2i2v upscale"
		});
		const t2i2vParameters = {
			...this.config.t2i2vDefaultParameters,
			...t2i2v
		};
		const result = this._pricingI2vPro(meter, {
			...rest,
			i2vPro: t2i2vParameters,
			t2i2v: void 0
		});
		const model = t2i2vParameters.model;
		const [realModel] = typeof model === "string" ? model.split("-") : [];
		if (!this._isChat(parameters) && realModel === "v3.0.2") result.addItem((i) => i + this._getBasePrice(), {
			key: "t2i2v-ltx23-image-base-adjustment",
			name: "t2i2v ltx23 image base adjustment",
			hidden: true
		});
		return result;
	}
	_pricingI2vPro(meter, parameters) {
		const actualBasePrice = this._getBasePrice();
		const clonedI2vProParameters = cloneParameters(parameters.i2vPro);
		clonedI2vProParameters.generateAudio = clonedI2vProParameters.generateAudio ?? false;
		if (clonedI2vProParameters.model) {
			const [realModelId] = (clonedI2vProParameters.model ?? "").split("-");
			clonedI2vProParameters.model = realModelId;
		}
		const priceList = cloneParameters(this.config.i2vProPriceList ?? []);
		priceList.push({
			id: "unknown",
			parameters: {},
			price: 5e4
		});
		priceList.forEach((p) => {
			p.price -= actualBasePrice;
			if (p.originalPrice) p.originalPrice -= actualBasePrice;
		});
		const lookupI2vProPrice = (parameters, sceneId) => {
			const matchedPrices = priceList.filter((p) => {
				if (p.sceneId !== sceneId) return false;
				return Object.entries(p.parameters).every(([key, value]) => parameters[key] === value);
			});
			if (!matchedPrices.length) return;
			return matchedPrices.sort((a, b) => Object.keys(b.parameters).length - Object.keys(a.parameters).length)[0];
		};
		const originalPriceItem = lookupI2vProPrice(clonedI2vProParameters);
		if (originalPriceItem) {
			const id = originalPriceItem.id;
			const price = originalPriceItem.originalPrice ?? originalPriceItem.price;
			meter.addItem((i) => i + price, {
				key: `i2v-pro-${id}`,
				name: `i2v pro ${id}`
			});
		}
		if (originalPriceItem) {
			const originalPrice = originalPriceItem.originalPrice ?? originalPriceItem.price;
			const discountedPrice = lookupI2vProPrice(clonedI2vProParameters, parameters.sceneId);
			if (discountedPrice?.price && discountedPrice?.price !== originalPrice) {
				const { id, sceneId, price: actualPrice } = discountedPrice;
				const price = originalPrice - actualPrice;
				meter.addItem((i) => i - price, {
					key: `i2v-pro-${id}-${sceneId}-discount`,
					name: `i2v pro ${id} ${sceneId} discount`
				});
			}
		}
		return meter;
	}
	_isI2vPro(parameters) {
		return parameters.i2vPro != null;
	}
	_isT2i2v(parameters) {
		return parameters.t2i2v != null;
	}
	_pricingChat(meter, parameters) {
		const CHAT_EDITING_DEFAULT_PRICE = 3e3;
		const BASE_PRICE = this.config.basePrice;
		const chatParameters = cloneParameters(parameters["chat"]);
		const priceList = cloneParameters(this.config.chatPriceList ?? []);
		const modelId = chatParameters.modelId;
		const chatPrice = priceList.find((p) => p.modelId === modelId) ?? {
			modelId: "default",
			price: CHAT_EDITING_DEFAULT_PRICE,
			originalPrice: CHAT_EDITING_DEFAULT_PRICE
		};
		const priceToAdd = chatPrice.originalPrice ? chatPrice.originalPrice - BASE_PRICE : chatPrice.price - BASE_PRICE;
		meter.addItem((i) => i + priceToAdd, {
			key: `chat-price-${chatPrice.modelId}`,
			name: `chat price ${chatPrice.modelId}`
		});
		if (chatPrice.originalPrice && chatPrice.originalPrice !== chatPrice.price) {
			const diff = chatPrice.originalPrice - chatPrice.price;
			meter.addItem((i) => i - diff, {
				key: `chat-price-${chatPrice.modelId}-discount`,
				name: `chat price ${chatPrice.modelId} discount`
			});
		}
		if (chatPrice.inputImageCount) {
			const { freeImages, step, pricePerStep } = chatPrice.inputImageCount;
			const inputImageCount = chatParameters.mediaIds?.length || 0;
			const pricingImages = Math.max(0, inputImageCount - freeImages);
			const price = Math.ceil(pricingImages / step) * pricePerStep;
			if (price > 0) meter.addItem((i) => i + price, {
				key: `chat-price-${chatPrice.modelId}-input-images`,
				name: `chat price ${chatPrice.modelId} input images`
			});
		}
		if (chatPrice.outputImageResolution) {
			const currentSelectedResolution = chatParameters.modelConfig?.resolution;
			if (currentSelectedResolution) {
				const resolutionPriceItem = chatPrice.outputImageResolution.find((r) => r.resolution === currentSelectedResolution);
				if (resolutionPriceItem) {
					const price = resolutionPriceItem.price;
					meter.addItem((i) => i + price, {
						key: `chat-price-${chatPrice.modelId}-output-resolution`,
						name: `chat price ${chatPrice.modelId} output resolution`
					});
				}
			}
		}
		return meter;
	}
	_isChat(parameters) {
		return parameters.chat != null;
	}
	_pricing() {
		const { workflow, workflowName, inputs } = this.parameters;
		const { highPriority } = this.config;
		const meter = this._getMeterWithBasePrice();
		if (workflowName?.startsWith("a/ipa")) return this._pricingBaseParameters(meter, inputs || {});
		if (workflowName?.startsWith("komiyama/inpaint-")) return this._pricingBaseParameters(meter, this.parameters, { isSDXL: true });
		if (this._isT2i2v(this.parameters)) return this._pricingT2i2v(meter, this.parameters);
		if (this._isI2vPro(this.parameters)) return this._pricingI2vPro(meter, this.parameters);
		if (this._isChat(this.parameters)) return this._pricingChat(meter, this.parameters);
		if (this.config.workflowPrepaidPrice && workflowName) {
			const [workflowNameOnly] = workflowName.split(":");
			if (workflowName in this.config.workflowPrepaidPrice) return this._pricingPrepaidWorkflow(meter, workflowName);
			else if (workflowNameOnly in this.config.workflowPrepaidPrice) return this._pricingPrepaidWorkflow(meter, workflowNameOnly);
		}
		if (workflow) {
			const meter = getPriceMeter();
			meter.type = PriceType.postpaid;
			this._priorityChannelPrice(meter);
			meter.addItem((i) => i - highPriority, {
				key: "workflow-priority-discount",
				name: "workflow priority discount"
			});
			return meter;
		}
		return this._pricingBaseParameters(meter, this.parameters);
	}
	getScenario() {
		return this.adapter.getScenario();
	}
	toPriceInfo() {
		return this.toPriceInfoV2();
	}
	toPriceInfoV2() {
		return this.adapter.getPriceInfo();
	}
	toPriceInfoV1() {
		return this._pricing().toPriceInfo();
	}
};
//#endregion
export { ParametersDefaults, Pricing, PriorityChannel };
